import {Component, OnInit, OnDestroy, ViewChild} from '@angular/core';
import {AdxPaymentService} from '../../service/adx-payment.service';
import {ReconciliationUploadModalComponent} from '../reconciliation-upload-modal/reconciliation-upload-modal.component';
import {MatDialog} from '@angular/material/dialog';
import {PaymentReconciliationFilter, ReconciliationModel} from '../../models/client-reconciliation';
import {ClientPaymentsEditModalComponent} from '../client-payments-edit-modal/client-payments-edit-modal.component';
import {Subscription, of} from 'rxjs';
import {GridModel, GridEventOutput} from '../../../shared/model/page-grid';
import {PageGridComponent} from '../../../shared/components/page-grid/page-grid.component';
import {ClientPaymentsReconciliationGridService} from '../../service/client-payments-reconciliation-grid.service';

@Component({
    selector: 'app-client-payments-reconciliation',
    templateUrl: './client-payments-reconciliation.component.html',
    styleUrls: ['./client-payments-reconciliation.component.scss'],
    standalone: false
})
export class ClientPaymentsReconciliationComponent implements OnInit, OnDestroy {
  @ViewChild(PageGridComponent) pageGridComponentRef?: PageGridComponent;

  reconciliationFilter$?: Subscription;
  refreshGridSubscription$?: Subscription;
  protected reconciliationGrid: GridModel<ReconciliationModel> = {
    gridDataTestId: 'client-payments-reconciliation-list',
    gridTitle: 'Payments Reconciliation',
    replayButton: true,
    showActionColumn: true,
    pdfButton: true,
    eventButton: { text: 'Upload File', icon: 'upload_file' },
    rowClass: (item: ReconciliationModel) => {
      return item.fmsPaymentAmountTotal && item.onFileAmountTotal !== item.fmsPaymentAmountTotal ? 'mismatched-payment-row' : '';
    },
    columns: [
      { columnProperty: 'clientNameAlias', columnHeader: 'Name', sort: true },
      { columnProperty: 'reconType', columnHeader: 'Type', sort: true, hideOnSmallScreen: true },
      { columnProperty: 'payeeName', columnHeader: 'Payee Name', sort: true },
      { columnProperty: 'lastFour', columnHeader: 'Card Last Four', sort: true },
      { columnProperty: 'onFileAmountTotal', columnHeader: 'On File Total', sort: true },
      { columnProperty: 'fmsPaymentAmountTotal', columnHeader: 'Fms Payments Total', sort: true },
      { columnProperty: 'adxReference', columnHeader: 'Trip Record Locator', sort: true, clickable: false },
      { columnProperty: 'agentName', columnHeader: 'Agent Name', sort: true, hideOnSmallScreen: true },
      { columnProperty: 'vendorName', columnHeader: 'Vendor Name', sort: true },
      { columnProperty: 'bankNo', columnHeader: 'Bank No', sort: true, hideOnSmallScreen: true },
      { columnProperty: 'bankName', columnHeader: 'Bank Name', sort: true, hideOnSmallScreen: true },
      { columnProperty: 'dataSourceName', columnHeader: 'Source Name', sort: true, hideOnSmallScreen: true },
      { columnProperty: 'dataSourceId', columnHeader: 'Source Number', sort: true }
    ],
    filters: [
      {
        filterProperty: 'reconType',
        filterDisplayName: 'Type',
        filterType: 'DROPDOWN',
        config: {
          dropdownOptions: of([
            { value: 'I', viewValue: 'Client' },
            { value: 'V', viewValue: 'Vendor' }
          ])
        }
      },
      {
        filterProperty: 'processorIds',
        filterDisplayName: 'Processor',
        filterType: 'DROPDOWN',
        config: {
          dropdownOptions: of([
            { value: 1, viewValue: 'Connexpay' },
            { value: 2, viewValue: 'Braintree' }
          ])
        }
      },
      {
        filterProperty: 'adxReference',
        filterDisplayName: 'Trip Record Locator (Reconciliation)',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'ReconciliationView',
          autocompleteColumnName: 'AdxReference',
          autocompleteEmitText: true
        }
      },
      {
        filterProperty: 'lastFourCC',
        filterDisplayName: 'Card Last Four',
        filterType: 'TEXT'
      },
      {
        filterProperty: 'isMatched',
        filterDisplayName: 'Matched',
        filterType: 'RADIOBUTTON',
        config: {
          radioButtonBoolean: true
        }
      },
      {
        filterProperty: 'releaseDateFrom',
        filterDisplayName: 'Release Date From',
        filterType: 'DATE'
      },
      {
        filterProperty: 'releaseDateTo',
        filterDisplayName: 'Release Date To',
        filterType: 'DATE'
      }
    ],
    gridGetService: this.clientPaymentsReconciliationGridService
  };

  constructor(
    private adxPaymentService: AdxPaymentService,
    private dialog: MatDialog,
    private clientPaymentsReconciliationGridService: ClientPaymentsReconciliationGridService
  ) {}

  ngOnInit(): void {
    this.reconciliationFilter$ = this.adxPaymentService.getPaymentsReconciliationFilter().subscribe((filter: PaymentReconciliationFilter) => {
      if (filter && Object.keys(filter).length > 0) {
        this.reconciliationGrid = {
          ...this.reconciliationGrid,
          filtersInitial: filter
        };
      }
    });
    this.refreshGridSubscription$ = this.adxPaymentService.refreshReconciliationGrid$.subscribe(() => {
      if (this.pageGridComponentRef) {
        this.pageGridComponentRef.getItems();
      }
    });
  }

  openEditModal(element: ReconciliationModel): void {
    this.dialog.open(ClientPaymentsEditModalComponent, { data: element });
  }

  uploadFileDialog(): void {
    this.dialog.open(ReconciliationUploadModalComponent).afterClosed().subscribe((sentFile: boolean) => {
      if (sentFile && this.pageGridComponentRef) {
        this.pageGridComponentRef.getItems();
      }
    });
  }

  handleGridAction({ item }: GridEventOutput<ReconciliationModel>): void {
    this.openEditModal(item);
  }

  ngOnDestroy(): void {
    if (this.reconciliationFilter$) {
      this.reconciliationFilter$.unsubscribe();
    }
    if (this.refreshGridSubscription$) {
      this.refreshGridSubscription$.unsubscribe();
    }
  }
}
